import React from 'react';
import { useTranslate, usePermissions } from 'react-admin';
import { useSelector, useDispatch } from 'react-redux';
import { Paper } from '@material-ui/core';

import OperationTypeField from '~/components/OperationTypeField';

import { VirtualizedList, Title, AmountField, TextField } from '~/components';
import {
  selectors,
  getVirtualizedListBegin,
  getVirtualizedListSuccess,
  getVirtualizedListFail,
} from '~/ducks/virtualizedList';
import { Column } from './components/list/Column';
import Filters from './components/list/ListFilters';
import { NoPermissions } from '~/components/ra';
import theme from '~/theme';
import ListDateTimeField from './components/list/ListDateTimeField';
import { CellProps } from './components/list/CellProps';
import VirtualizedListWrapper from '~/components/VirtualizedListWrapper';
import { Transfer, TransfersFilters } from '~/types/Transfer';
import { getTransfers } from '~/api/transfers';
import { hasTranslation } from '~/utils';
import ListReportingStatusField from './components/list/ListReportingStatusField';

export const resource = 'transaction/transfers/search';
export const i18nPath = 'resources.transfers.';
export const tcLabel = (key: string) => `${i18nPath}transferCategories.${key}`;

interface Props {
  permissions: string[];
  history: any;
}

const TransactionList = (props: Props) => {
  const { history } = props;
  const { permissions } = usePermissions();

  const translate = useTranslate();
  const dispatch = useDispatch();
  const filters = useSelector(selectors.getFilters<TransfersFilters>(resource));
  const list = useSelector(selectors.getList(resource));

  const loadNextPage = async () => {
    if (isNextPageLoading || (!list.cursors.next && list.data.length > 0)) return;

    const queryToken = Math.random();
    try {
      dispatch(getVirtualizedListBegin({ resource, queryToken }));

      const { data } = await getTransfers({
        next: list.cursors.next,
        // filters, except ledgerSystem and ledgerSystemStatus
        filters: {
          ...filters,
          ledgerSystem: undefined,
          ledgerSystemStatus: undefined,
        },
      });
      const responseData = {
        ...data,
        cursors: data.cursors,
        records: data.transfers as any[],
      };

      dispatch(getVirtualizedListSuccess({ resource, responseData, queryToken }));
    } catch (error) {
      const err = error as Error;
      dispatch(getVirtualizedListFail({ resource, error: err, queryToken }));
    }
  };
  const columns: Column[] = [
    {
      selectCellData: (t: Transfer) => t.postedAt,
      cellDataWrapperComponent: RowField(ListDateTimeField),
      header: translate(i18nPath + 'list.postedAt'),
      style: {
        flexBasis: 150,
        minWidth: 100,
        maxWidth: 150,
      },
    },
    {
      selectCellData: (t: Transfer) => ({ id: t.fromAccountId, number: t.fromAccountNumber }),
      cellDataWrapperComponent: RowField((props: CellProps) => {
        return (
          <TextField
            style={{ fontWeight: 400 }}
            showLink={props.children?.id ? `/accounts/${props.children?.id}/show` : undefined}
          >
            {props.children?.number}
          </TextField>
        );
      }),
      header: translate(i18nPath + 'list.fromAccountNumber'),
      style: {
        flexBasis: 180,
        minWidth: 180,
        maxWidth: 200,
      },
    },
    {
      selectCellData: (t: Transfer) => ({ id: t.toAccountId, number: t.toAccountNumber }),
      cellDataWrapperComponent: RowField((props: CellProps) => (
        <TextField
          style={{ fontWeight: 400 }}
          showLink={props.children?.id ? `/accounts/${props.children?.id}/show` : undefined}
        >
          {props.children?.number}
        </TextField>
      )),
      header: translate(i18nPath + 'list.toAccountNumber'),
      style: {
        flexBasis: 180,
        minWidth: 180,
        maxWidth: 200,
      },
    },
    {
      selectCellData: (t: Transfer) => t.amount,
      cellDataWrapperComponent: RowField((props: CellProps) => (
        <AmountField amount={props.children} style={{ fontWeight: 400 }} />
      )),
      header: translate(i18nPath + 'list.amount'),
      style: {
        flexBasis: 100,
        minWidth: 100,
        maxWidth: 250,
      },
    },
    {
      selectCellData: (t: Transfer) => t.transferOperationType,
      cellDataWrapperComponent: RowField((props: CellProps) => {
        return <OperationTypeField operationType={props.children} />;
      }),
      header: translate(i18nPath + 'list.transferOperationType'),
      width: 200,
    },
    {
      selectCellData: (t: Transfer) => t.transferCategory,
      cellDataWrapperComponent: RowField((props: CellProps) =>
        props.children
          ? hasTranslation(tcLabel(props.children))
            ? translate(tcLabel(props.children))
            : props.children
          : null
      ),
      header: translate(i18nPath + 'list.transferCategory'),
      style: {
        flexBasis: 150,
        minWidth: 100,
        maxWidth: 250,
      },
    },
    {
      selectCellData: (t: Transfer) => t.ledgerMappingStatusBySystem,
      cellDataWrapperComponent: RowField(ListReportingStatusField),
      header: translate(i18nPath + 'list.ledgerMappingStatusBySystem'),
      style: {
        flexBasis: 150,
        minWidth: 100,
        maxWidth: 250,
      },
    },
  ];

  const viewPermission = permissions?.includes('transaction.transfers.view');

  const getRowLink = (data: Transfer) => {
    if (viewPermission) {
      return `/transaction/transfers/${data.id}/show`;
    }
  };
  const { hasNextPage, isNextPageLoading, data } = list;
  if (!permissions) return null;
  if (!permissions.includes('transaction.transfers.list')) return <NoPermissions />;
  return (
    <>
      <VirtualizedListWrapper
        title={
          <Title
            resource={resource}
            headline={translate(i18nPath + 'list.title')}
            subtitle={translate(i18nPath + 'subtitle')}
          />
        }
        filters={<Filters cleanFilters={history.action !== 'POP'} />}
      >
        <Paper style={{ flex: '1 1 auto', overflow: 'hidden', ...fixBottomShadow }}>
          <VirtualizedList<Transfer>
            resource={resource}
            columns={columns}
            hasNextPage={hasNextPage}
            isNextPageLoading={isNextPageLoading}
            filters={filters}
            data={data}
            loadNextPage={loadNextPage}
            outterTable
            rowHeight={48}
            getRowLink={getRowLink}
            minHeight={0}
          />
        </Paper>
      </VirtualizedListWrapper>
    </>
  );
};

export const RowField = (Field: any) => {
  return (props: CellProps) => {
    return (
      <Field {...props} style={{ color: theme.palette.text.primary }}>
        {props?.children}
      </Field>
    );
  };
};

const fixBottomShadow = { paddingBottom: 2 };

export default TransactionList;
